import { useQuery, UseQueryResult } from "@tanstack/react-query";

import { useContractService } from "contexts/ServiceContext";
import { QuotationModel } from "api";
import { AI_EVENTS, useTrackEvent } from "../../useAppInsight";
import useSelectedCustomerCode from "hooks/useSelectedCustomerCode";

export const CONTRACTS_QUERY_KEY = "contracts";

const useContracts = (
  searchTerm = "",
  filters?: Partial<ContractListFilters>,
  currentSort?: { key: keyof QuotationModel; direction: string }
): UseQueryResult<Contract[]> => {
  const cardCodes = useSelectedCustomerCode();
  const contractService = useContractService();
  const trackQuery = useTrackEvent(AI_EVENTS.API_REQUEST, {
    cardCodes,
  });
  const trackQueryError = useTrackEvent(AI_EVENTS.API_ERROR, {
    cardCodes,
  });

  return useQuery({
    queryKey: [
      CONTRACTS_QUERY_KEY,
      { customer: cardCodes },
      { filters, currentSort, searchTerm },
    ],
    queryFn: () => {
      const eventId = trackQuery({
        endpoint: "Quotation",
        parameters: { filters, sorting: currentSort, searchTerm },
      });
      return cardCodes
        ? contractService
            .getContractList(
              cardCodes,
              searchTerm,
              {
                ...filters,
                shipStartDate: filters?.shipStartDate
                  ? new Date(
                      Date.UTC(
                        filters.shipStartDate.getUTCFullYear(),
                        filters.shipStartDate.getUTCMonth(),
                        1
                      )
                    )
                  : undefined,
                shipEndDate: filters?.shipEndDate
                  ? new Date(
                      Date.UTC(
                        filters.shipEndDate.getUTCFullYear(),
                        filters.shipEndDate.getUTCMonth() + 1,
                        0
                      )
                    ) // Inclusive, so use end of the month. Month + 1 w/ Date 0 becomes end of the month
                  : undefined,
                statuses: filters?.statuses ?? [],
                products: filters?.products ?? [],
                signature: filters?.signature ?? [],
              },
              currentSort
            )
            .catch((err) => {
              trackQueryError(
                {
                  statusCode: err.statusCode,
                  message: err.message,
                },
                eventId
              );
              throw err;
            })
        : Promise.reject();
    },
    enabled: cardCodes.length > 0,
    select: (data) => data.value,
  });
};

export const useContractsWithEsign = (
  cardCodes: string[]
): UseQueryResult<Contract[]> => {
  const contractService = useContractService();
  const trackQuery = useTrackEvent(AI_EVENTS.API_REQUEST, {
    cardCode: cardCodes,
  });
  const trackQueryError = useTrackEvent(AI_EVENTS.API_ERROR, {
    cardCode: cardCodes,
  });

  return useQuery({
    queryKey: [CONTRACTS_QUERY_KEY, { customer: cardCodes }],
    queryFn: () => {
      const eventId = trackQuery({
        endpoint: "Quotation",
      });
      return cardCodes
        ? contractService
            .getContractListWithEsignAvailable(cardCodes)
            .catch((err) => {
              trackQueryError(
                {
                  statusCode: err.statusCode,
                  message: err.message,
                },
                eventId
              );
              throw err;
            })
        : Promise.reject();
    },
    enabled: cardCodes.length > 0,
    select: (data) => data.value,
  });
};

export default useContracts;
